import React, { useState, useEffect } from 'react';

// Placeholder for baseUrl, as it's imported from '../../config' in the original code.
// In a real application, ensure this is correctly configured to your API endpoint.
import baseUrl from '../../config';

const GetBannerPage = () => {
  const [bannerImageUrl, setBannerImageUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  // Constants for managing popup timing in local storage
  const LAST_POPUP_TIMESTAMP_KEY = 'lastPopupShownTimestamp';
  const POPUP_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes in milliseconds

  /**
   * Fetches the current banner image from the API.
   * If successful, sets the image URL and opens the popup,
   * then records the current timestamp in local storage.
   */
  const fetchCurrentImage = async () => {
    setLoading(true); // Indicate loading process has started
    setError(null);    // Clear any previous errors

    try {
      const response = await fetch(baseUrl + 'api/banner/getbanner'); // Make API call
      if (response.ok) {
        // Assuming the API returns the image directly, or a path to it.
        // If the API returns JSON with an image URL, you'd parse response.json()
        // and extract the URL. For now, assuming the fetch URL is the image URL.
        const imageUrl = baseUrl + 'api/banner/getbanner';
        setBannerImageUrl(imageUrl); // Set the image URL state
        setIsPopupOpen(true);        // Open the popup

        // Record the timestamp in local storage when the popup is successfully shown
        localStorage.setItem(LAST_POPUP_TIMESTAMP_KEY, Date.now().toString());
      } else {
        setBannerImageUrl(''); // Clear the image URL
        setIsPopupOpen(false); // Ensure popup is closed
        if (response.status === 404) {
          setError('No banner image has been uploaded yet.');
        } else {
          setError('Failed to fetch banner image from the server. Status: ' + response.status);
        }
      }
    } catch (err) {
      // Handle network or other errors during fetch
      console.error('Network error fetching current image:', err);
      setError('Network error: Could not connect to the server.');
      setBannerImageUrl('');
      setIsPopupOpen(false);
    } finally {
      setLoading(false); // Indicate loading process has finished
    }
  };

  /**
   * Checks if enough time has passed since the last popup display
   * and triggers fetching the image if conditions are met.
   */
  const checkAndShowPopup = () => {
    const lastShownTimestamp = localStorage.getItem(LAST_POPUP_TIMESTAMP_KEY);
    const currentTime = Date.now();

    // If no timestamp exists (first visit) or 5 minutes have passed, fetch and show popup
    if (!lastShownTimestamp || (currentTime - parseInt(lastShownTimestamp, 10)) >= POPUP_INTERVAL_MS) {
      fetchCurrentImage();
    } else {
      // If the popup was shown recently, ensure it is closed (e.g., if user navigated back)
      setIsPopupOpen(false);
    }
  };

  // useEffect hook to manage the popup's lifecycle
  useEffect(() => {
    // Perform an initial check when the component mounts
    checkAndShowPopup();

    // Set up an interval to periodically check and show the popup every 5 minutes
    const intervalId = setInterval(checkAndShowPopup, POPUP_INTERVAL_MS);

    // Clean up the interval when the component unmounts to prevent memory leaks
    return () => clearInterval(intervalId);
  }, []); // Empty dependency array ensures this effect runs only once on mount and cleans up on unmount

  /**
   * Handles closing the popup. Clears the image URL and error state.
   */
  const handleClosePopup = () => {
    setIsPopupOpen(false); // Close the modal
    setBannerImageUrl(''); // Clear image URL to prevent it from flashing before the next fetch
    setError(null);        // Clear any display errors
  };

  // --- Loading State UI ---
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 font-inter">
        <div className="flex flex-col items-center p-8 bg-white rounded-lg shadow-lg">
          {/* Tailwind spinner icon */}
          <svg className="animate-spin h-10 w-10 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-700">Loading banner...</p>
        </div>
      </div>
    );
  }

  // --- Error State UI (only shown if there's an error and no image to display) ---
  if (error && !bannerImageUrl && !loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4 font-inter">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative shadow-md" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline ml-2">{error}</span>
          {/* Refresh button to retry fetching */}
          <button
            onClick={fetchCurrentImage}
            className="ml-4 text-red-700 hover:text-red-900 font-bold focus:outline-none transition-colors duration-200"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  // --- Main Component Render ---
  return (
    <>
      {/* The modal overlay, conditionally rendered */}
      {isPopupOpen && bannerImageUrl && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4 font-inter">
          {/* The modal content container */}
          <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden max-w-2xl w-full mx-auto transform transition-all duration-300 scale-100">
            {/* Close button with SVG icon */}
            <button
              onClick={handleClosePopup}
              className="absolute top-4 right-4 bg-white bg-opacity-70 hover:bg-opacity-90 rounded-full p-2 text-gray-600 hover:text-gray-900 focus:outline-none transition-all duration-200 z-10"
              aria-label="close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Image display area */}
            <div className="w-full max-h-[70vh] flex items-center justify-center overflow-hidden bg-gray-100">
              <img
                src={bannerImageUrl}
                alt="Current Banner"
                className="max-w-full max-h-full object-contain block rounded-lg"
                onError={(e) => {
                  // Fallback for image loading errors
                  e.target.onerror = null; // Prevent infinite loop if fallback also fails
                  e.target.src = "https://placehold.co/600x400/FF0000/FFFFFF?text=Image+Load+Error";
                  setError('Error loading image. It might be corrupted or deleted from server.');
                  console.error('Image failed to load:', e.target.src);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GetBannerPage;
